/**
 * Phase definitions for the financial SmartLoaders. Timings mirror the real
 * backend chains — tune startSec/expectedSec against observed latency, since
 * fabricated timings are the one way a staged loader can lie.
 */
import { Building2, FileSearch, Sparkles, Activity, Grid3x3, BarChart3, LayoutGrid } from 'lucide-react';
import type { SmartLoaderPhase } from '../ui/SmartLoader';

/** Company financial dashboard load. Elapsed mode, ~8s. */
export const FINANCIAL_LOAD_PHASES: SmartLoaderPhase[] = [
  { id: 'resolve', label: 'Resolving company', hint: 'Matching the company in the graph', icon: Building2, startSec: 0 },
  { id: 'reports', label: 'Loading filings', hint: 'Fetching up to 10 years of reports', icon: FileSearch, startSec: 2 },
  { id: 'metrics', label: 'Assembling metrics', hint: 'EV, EBITDA, WACC, ROIC, CAGR', icon: Sparkles, startSec: 5 },
];
export const FINANCIAL_LOAD_EXPECTED_SEC = 8;

/** Combined sensitivity analysis. Elapsed mode, ~30s, violet accent. */
export const SENSITIVITY_PHASES: SmartLoaderPhase[] = [
  { id: 'base', label: 'Loading base metrics', hint: 'Revenue, EBITDA, IC, WACC, CAP per year', icon: Activity, startSec: 0 },
  { id: 'scenarios', label: 'Running scenario grid', hint: 'Revenue × EBITDA × IC × WACC × CAP combinations', icon: Grid3x3, startSec: 4 },
  { id: 'tornado', label: 'Ranking sensitivities', hint: 'Elasticity + sensitivity index per parameter', icon: BarChart3, startSec: 12 },
  { id: 'heatmaps', label: 'Building heatmaps', hint: 'IC, WACC and CAP sections', icon: LayoutGrid, startSec: 20 },
];
export const SENSITIVITY_EXPECTED_SEC = 30;

/**
 * Peer fan-out phases — one per peer, with REAL per-peer status (peers resolve
 * out of order, so an ordered activeIdx would be wrong). Pair with
 * `{ mode: 'count', completed, total }`.
 */
export function buildPeerPhases(
  names: string[],
  loadedByName: Record<string, unknown>,
  failedNames: string[],
): SmartLoaderPhase[] {
  const failed = new Set(failedNames);
  return names.map((name) => ({
    id: name,
    label: name,
    icon: Building2,
    status: loadedByName[name] ? 'done' : failed.has(name) ? 'failed' : 'active',
  }));
}
